import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { PRODUCTS, CATS, SIZES } from "./data";
import { useCart } from "./cart";
import { useLang } from "./i18n";
import { Reveal, SectionHead } from "./Reveal";

export const Card = ({ p, i, total = PRODUCTS.length, light = false }) => {
  const { add } = useCart();
  const { t } = useLang();
  const [size, setSize] = useState("M");
  return (
    <Reveal delay={(i % 3) * 0.08} className="group">
      <div data-testid={`product-card-${p.id}`} className="relative">
        <div className={`relative aspect-[4/5] overflow-hidden border og-frame ${light ? "bg-[#E9E9E9] border-black/10" : "bg-[#0D0D0D] border-white/10"}`}>
          <div className={`absolute inset-0 ${light ? "og-card-spot-light" : "og-card-spot"}`} />
          <Link to={`/products/${p.id}`} data-testid={`product-image-link-${p.id}`} className="absolute inset-0" aria-label={p.name}>
          {p.imgs.slice(0, 2).map((src, k) => (
            <img
              key={src}
              src={src}
              alt={p.name}
              loading="lazy"
              className={`absolute inset-0 w-full h-full transition-[opacity,transform] duration-700 ease-out group-hover:scale-[1.06] ${p.fit === "cover" ? "object-cover" : "object-contain p-8"} ${k === 1 ? "opacity-0 group-hover:opacity-100" : "group-hover:opacity-0"}`}
            />
          ))}
          </Link>
          <div className={`absolute top-3 left-3 f-mono text-[11px] tracking-[0.2em] pointer-events-none ${light ? "text-black/70" : "text-zinc-300"}`}>{String(i + 1).padStart(2, "0")}/{String(total).padStart(2, "0")}</div>
          {p.pre && (
            <div data-testid={`product-preorder-badge-${p.id}`} className="absolute top-3 right-3 bg-[#FF2A2A] text-black f-mono text-[11px] font-bold tracking-[0.15em] px-2 py-1 pointer-events-none">{t("prod.pre")}</div>
          )}
          <div className="absolute inset-x-0 bottom-0 p-3 md:translate-y-full md:group-hover:translate-y-0 transition-transform duration-500 ease-out">
            <div className="flex gap-1 bg-black/80 backdrop-blur-md p-1 border border-white/10">
              {SIZES.map((s) => (
                <button
                  key={s}
                  data-testid={`product-size-${p.id}-${s}`}
                  onClick={() => setSize(s)}
                  className={`flex-1 h-9 f-mono text-xs transition-colors ${size === s ? "bg-white text-black" : "text-white hover:bg-white/10"}`}
                >
                  {s}
                </button>
              ))}
              <button data-testid={`product-add-${p.id}`} onClick={() => add(p, size)} className="h-9 px-3 bg-[#FF2A2A] text-black hover:bg-white transition-colors" aria-label={t("prod.add")}>
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-start justify-between gap-4 pt-4">
          <Link to={`/products/${p.id}`} data-testid={`product-link-${p.id}`} className={`og-link f-disp uppercase text-2xl leading-none ${light ? "text-black" : "text-[#F5F5F7]"}`}>{p.name}</Link>
          <span data-testid={`product-price-${p.id}`} className={`f-mono text-sm tabular-nums ${light ? "text-black" : "text-zinc-300"}`}>${p.price}.00</span>
        </div>
        <div className={`f-mono text-[11px] tracking-[0.2em] mt-1 uppercase ${light ? "text-black/60" : "text-zinc-400"}`}>{p.cat}</div>
      </div>
    </Reveal>
  );
};

export const CatFilter = ({ cat, setCat, light = false, cats = CATS }) => {
  const { t } = useLang();
  const on = light ? "border-black text-white" : "border-white text-black";
  const off = light ? "border-black/20 text-black/70 hover:border-black" : "border-white/20 text-zinc-300 hover:border-white";
  return (
    <div className="flex flex-wrap gap-2">
      {cats.map((c) => (
        <button
          key={c}
          data-testid={`filter-${c.toLowerCase()}`}
          onClick={() => setCat(c)}
          className={`relative px-4 h-9 f-mono text-xs uppercase tracking-[0.15em] border transition-colors ${cat === c ? on : off}`}
        >
          {cat === c && <motion.span layoutId={`filter-pill-${light}`} className={`absolute inset-0 ${light ? "bg-black" : "bg-white"}`} transition={{ type: "spring", stiffness: 400, damping: 35 }} />}
          <span className="relative">{c === "all" ? t("prod.all") : c}</span>
        </button>
      ))}
    </div>
  );
};

export const Products = () => {
  const { t } = useLang();
  const [cat, setCat] = useState("all");
  const list = cat === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.cat === cat);
  return (
    <section id="nouveautes" data-testid="products-section" className="px-5 md:px-10 py-24 md:py-36 bg-[#050505]">
      <SectionHead
        idx="01"
        kicker={t("prod.kicker")}
        title={<>{t("prod.t1")}<span className="text-[#FF2A2A]">—</span><br />{t("prod.t2")}</>}
        right={<CatFilter cat={cat} setCat={setCat} />}
      />
      <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14">
        {list.map((p, i) => <Card key={p.id} p={p} i={i} />)}
      </motion.div>
    </section>
  );
};
